import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "./cityPricing";

const capDAil: ChauffeurPage = {
  slug: "cap-d-ail",
  title: "Chauffeur privé à Cap-d’Ail – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Cap-d’Ail : desserte de Monaco à deux pas, plage Mala, sentier littoral, gare et transfert aéroport de Nice en ~30 min.",
  keywords: [
    "chauffeur privé cap-d’ail",
    "vtc cap-d’ail",
    "chauffeur cap-d-ail monaco",
    "transfert cap-d’ail aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Cap-d’Ail",
    subtitle:
      "La commune française aux portes de Monaco : desserte de la Principauté, plage Mala et sentier du littoral, avec un chauffeur dédié.",
    keywords: [
      "VTC Cap-d’Ail",
      "chauffeur Monaco",
      "transfert aéroport Nice",
      "chauffeur plage Mala",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Cap-d’Ail",
    badges: ["Aux portes de Monaco", "Plage Mala", "~30 min de l’aéroport"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Cap-d’Ail",
    items: [
      {
        title: "Aux portes de Monaco",
        description:
          "Dernière commune avant la Principauté : desserte de Fontvieille, Monte-Carlo et du Rocher sans subir la circulation et le stationnement intra-Monaco.",
      },
      {
        title: "Plage Mala & criques",
        description:
          "La plage Mala se rejoint par de longs escaliers : le chauffeur vous dépose au plus haut et vous reprend au même point, sans détour de stationnement.",
      },
      {
        title: "Sentier littoral & gare",
        description:
          "De la Pointe des Douaniers à la gare de Cap-d’Ail : points de rendez-vous pratiques pour la marche côtière comme pour le train.",
      },
      {
        title: "À ~30 minutes de l’aéroport",
        description:
          "Transfert direct depuis et vers l’aéroport de Nice, par la basse corniche ou l’A8, avec suivi de vol.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Cap-d’Ail",
    services: [
      {
        title: "Desserte de Monaco",
        text: "Rendez-vous d’affaires, casino, restaurants et soirées à Monte-Carlo : dépose au plus près, sans chercher à se garer dans la Principauté.",
      },
      {
        title: "Transfert aéroport de Nice",
        text: "Cap-d’Ail ⇄ aéroport de Nice en ~30 min, avec suivi de vol et accueil à l’arrivée.",
      },
      {
        title: "Plage Mala & bord de mer",
        text: "Dépose en haut des escaliers de la plage Mala et le long du littoral, avec reprise sur appel.",
      },
      {
        title: "Événements de Monaco",
        text: "Grand Prix, Yacht Show et galas : navettes depuis Cap-d’Ail pour éviter les fermetures et l’affluence de la Principauté.",
      },
      {
        title: "Mise à disposition & corniches",
        text: "À l’heure ou à la journée pour relier Monaco, Èze, Beausoleil et Nice au fil des corniches.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Plage Mala",
        description:
          "Crique préservée, accessible par des escaliers depuis la ville haute.",
      },
      {
        name: "Sentier du littoral",
        description: "Chemin côtier de la Pointe des Douaniers, autour du cap.",
      },
      {
        name: "Gare de Cap-d’Ail",
        description:
          "Halte ferroviaire à un pas de Monaco, sur la ligne du littoral.",
      },
      {
        name: "Frontière de Monaco",
        description:
          "Accès à Fontvieille et Monte-Carlo, à la limite de la Principauté.",
      },
      {
        name: "Château des Terrasses",
        description: "Domaine et jardins en surplomb de la mer.",
      },
      {
        name: "Jardin des Oliviers",
        description: "Parc en bord de mer, sur le sentier vers Monaco.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur aux portes de la Principauté",
    subheading:
      "Cap-d’Ail vit au rythme de Monaco tout en gardant ses criques et son sentier du littoral.",
    paragraphs: [
      "Adossée à la Principauté, Cap-d’Ail est la porte française de Monaco : quelques minutes suffisent pour rejoindre Fontvieille ou Monte-Carlo. Plutôt que d’affronter la circulation et les parkings de Monaco, un chauffeur vous dépose au plus près et reste joignable pour la reprise.",
      "KS Rent Services fonctionne en forfaits fixes annoncés à l’avance — dont Cap-d’Ail ⇄ aéroport de Nice — et en mise à disposition à l’heure ou à la journée, avec devis immédiat. Nos chauffeurs bilingues français/anglais, basés sur la Côte d’Azur, sont notés 5,0/5 sur 38 avis Google.",
      "Entre la plage Mala nichée dans sa crique, le sentier du littoral autour du cap et la gare toute proche, Cap-d’Ail mêle vie balnéaire et proximité immédiate de Monaco : le chauffeur relie ces points sans que le stationnement n’entre en jeu.",
    ],
    bullets: [
      "Desserte de Monaco sans stationnement dans la Principauté",
      "Forfaits fixes & mise à disposition, devis immédiat",
      "Chauffeurs bilingues FR/EN, 5,0/5 sur 38 avis Google",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Cap-d’Ail",
    steps: [
      "Indiquez votre programme : Monaco, plage Mala, transfert aéroport ou événement.",
      "Nous confirmons le véhicule et le forfait ou la mise à disposition.",
      "Le chauffeur vous prend en charge à votre villa, hôtel ou à la gare.",
      "Ajustements en direct selon le trafic et les fermetures autour de Monaco.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Cap-d’Ail"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice ?",
      a: "Environ 30 minutes par la basse corniche ou l’A8. Le chauffeur suit votre vol et adapte l’heure de départ selon le trafic à l’entrée de Nice.",
    },
    {
      q: "Assurez-vous la desserte de Monaco depuis Cap-d’Ail ?",
      a: "Oui. Nous vous conduisons à Fontvieille, Monte-Carlo ou au Rocher et vous déposons au plus près, sans avoir à gérer le stationnement dans la Principauté.",
    },
    {
      q: "Intervenez-vous pendant le Grand Prix de Monaco ?",
      a: "Oui. Pendant le Grand Prix, le Yacht Show et les galas, nous organisons des navettes depuis Cap-d’Ail pour contourner les fermetures de rues et l’affluence.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez tôt lors des grands événements monégasques ; en temps normal, les demandes de dernière minute sont traitées selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à la clientèle internationale qui fréquente Monaco et Cap-d’Ail.",
    },
    {
      q: "Quels véhicules proposez-vous pour Cap-d’Ail ?",
      a: "Berline Mercedes, van 7 places ou SUV selon le nombre de passagers, adaptés aux allers-retours fréquents vers la Principauté.",
    },
  ],
  relatedLinks: [
    { label: "Réserver un chauffeur à Monaco", href: "/chauffeur/monaco" },
    {
      label: "Chauffeur aéroport de Nice",
      href: "/chauffeur/aeroport",
    },
    { label: "Chauffeur privé Nice", href: "/chauffeur/nice" },
    {
      label: "Chauffeur privé sur la Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Cap-d’Ail",
    text: "Desserte de Monaco, plage Mala ou transfert aéroport : réservez votre chauffeur.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Cap-d’Ail 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Cap-d’Ail : desserte de Monaco, plage Mala, sentier littoral et transfert aéroport de Nice en ~30 min. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/cap-d-ail",
  },
};

export default capDAil;
